import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PlusIcon, PencilSimpleIcon, TrashIcon, ArmchairIcon, MagnifyingGlassIcon, CalendarBlankIcon, PrinterIcon } from "@phosphor-icons/react";
import { usePermission } from "@/lib/permissions";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const SECTIONS = ["Banquet", "Conference", "Wedding", "Board room", "Private dining"];
const SECTION_TONE = {
  Banquet: "bg-primary/10 text-primary border-primary/30",
  Conference: "bg-secondary/15 text-secondary border-secondary/40",
  Wedding: "bg-[hsl(340_80%_92%)] text-[hsl(340_70%_35%)] border-[hsl(340_60%_60%)]",
  "Board room": "bg-muted text-muted-foreground",
  "Private dining": "bg-[hsl(45_82%_88%)] text-[hsl(38_62%_35%)] border-[hsl(45_82%_60%)]",
};
const EMPTY_HALL = { name: "", section: "Banquet", capacity: 50, hourly_rate: 0, day_rate: 0, description: "", is_active: true, pricing_type: "fixed", veg_plate_rate: 0, non_veg_plate_rate: 0, min_plates: 50 };
const EMPTY_BOOKING = { customer_name: "", customer_phone: "", purpose: "Wedding reception", start_at: "", end_at: "", guest_count: 50, package_notes: "", total_amount: 0, deposit_amount: 0, payment_status: "pending", veg_plates: 0, non_veg_plates: 0, veg_plate_rate: 0, non_veg_plate_rate: 0 };

export default function Halls() {
  const { activeId, active } = useProperty();
  const { canWrite } = usePermission("halls");
  const [halls, setHalls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [q, setQ] = useState("");

  // Hall dialog
  const [hallOpen, setHallOpen] = useState(false);
  const [hallForm, setHallForm] = useState(EMPTY_HALL);
  const [editing, setEditing] = useState(null);
  const [editBooking, setEditBooking] = useState(null);

  // Booking dialog
  const [bookOpen, setBookOpen] = useState(false);
  const [bookForm, setBookForm] = useState(EMPTY_BOOKING);
  const [selectedHallId, setSelectedHallId] = useState("");

  const load = () => {
    if (!activeId) return;
    api.get("/halls", { params: { property_id: activeId } }).then((r) => setHalls(r.data));
    api.get("/halls/bookings", { params: { property_id: activeId } }).then((r) => setBookings(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const saveHall = async () => {
    if (!hallForm.name) return toast.error("Hall name required");
    try {
      if (editing) {
        await api.patch(`/halls/${editing.id}`, hallForm);
        toast.success("Hall updated");
      } else {
        await api.post("/halls", { ...hallForm, property_id: activeId });
        toast.success("Hall created");
      }
      setHallOpen(false); setEditing(null); setHallForm(EMPTY_HALL);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeHall = async (h) => {
    if (!window.confirm(`Delete ${h.name}?`)) return;
    try { await api.delete(`/halls/${h.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const selectedHall = useMemo(() => halls.find((h) => h.id === selectedHallId), [halls, selectedHallId]);
  const isPlatePricing = selectedHall?.pricing_type === "per_plate";
  const computedPlateTotal = isPlatePricing
    ? (Number(bookForm.veg_plates || 0) * Number(bookForm.veg_plate_rate || selectedHall?.veg_plate_rate || 0)
       + Number(bookForm.non_veg_plates || 0) * Number(bookForm.non_veg_plate_rate || selectedHall?.non_veg_plate_rate || 0))
    : 0;

  const saveBooking = async () => {
    if (!selectedHallId) return toast.error("Pick a hall");
    if (!bookForm.customer_name || !bookForm.start_at || !bookForm.end_at) return toast.error("Customer, start & end required");
    const payload = { ...bookForm, hall_id: selectedHallId, property_id: activeId };
    if (isPlatePricing) {
      // Auto-fill total from plate math if user didn't override
      const rate_v = Number(bookForm.veg_plate_rate) || Number(selectedHall.veg_plate_rate) || 0;
      const rate_n = Number(bookForm.non_veg_plate_rate) || Number(selectedHall.non_veg_plate_rate) || 0;
      payload.veg_plate_rate = rate_v;
      payload.non_veg_plate_rate = rate_n;
      const total = Number(bookForm.veg_plates || 0) * rate_v + Number(bookForm.non_veg_plates || 0) * rate_n;
      payload.total_amount = total;
    }
    try {
      await api.post("/halls/bookings", payload);
      toast.success("Booking created");
      setBookOpen(false); setBookForm(EMPTY_BOOKING); setSelectedHallId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking ${b.booking_ref}?`)) return;
    await api.patch(`/halls/bookings/${b.id}`, { status: "cancelled" });
    toast.success("Cancelled"); load();
  };

  const filteredBookings = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return bookings;
    return bookings.filter((b) =>
      (b.booking_ref || "").toLowerCase().includes(term) ||
      (b.customer_name || "").toLowerCase().includes(term) ||
      (b.purpose || "").toLowerCase().includes(term) ||
      (halls.find((h) => h.id === b.hall_id)?.name || "").toLowerCase().includes(term)
    );
  }, [bookings, halls, q]);

  return (
    <div>
      <PageHeader
        overline={active ? active.name : "Property"}
        title="Halls & Events"
        description="Configure banquet / conference / wedding halls and manage their bookings across dates."
      />

      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList className="mb-6">
          <TabsTrigger value="bookings" data-testid="halls-tab-bookings"><CalendarBlankIcon size={14} className="mr-1" />Bookings</TabsTrigger>
          <TabsTrigger value="halls" data-testid="halls-tab-halls"><ArmchairIcon size={14} className="mr-1" />Halls</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings">
          <Card className="p-4 mb-4 flex justify-between flex-wrap gap-3">
            <div className="flex-1 min-w-[240px] relative">
              <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search by ref, customer, purpose, hall…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="halls-search" />
            </div>
            {canWrite && (
              <Dialog open={bookOpen} onOpenChange={setBookOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="halls-new-booking-btn"><PlusIcon size={14} className="mr-1" />New booking</Button>
                </DialogTrigger>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>New hall booking</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Hall</Label>
                      <Select value={selectedHallId} onValueChange={setSelectedHallId}>
                        <SelectTrigger data-testid="halls-book-hall-select"><SelectValue placeholder="Pick a hall" /></SelectTrigger>
                        <SelectContent>
                          {halls.filter((h) => h.is_active).map((h) => (
                            <SelectItem key={h.id} value={h.id}>{h.name} · {h.section} · {h.capacity} guests · {inr(h.hourly_rate)}/hr</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Customer *</Label><Input value={bookForm.customer_name} onChange={(e) => setBookForm({ ...bookForm, customer_name: e.target.value })} data-testid="halls-book-customer" /></div>
                    <div><Label>Phone</Label><Input value={bookForm.customer_phone} onChange={(e) => setBookForm({ ...bookForm, customer_phone: e.target.value })} data-testid="halls-book-phone" /></div>
                    <div><Label>Purpose</Label><Input value={bookForm.purpose} onChange={(e) => setBookForm({ ...bookForm, purpose: e.target.value })} placeholder="e.g. Wedding, Corporate off-site" data-testid="halls-book-purpose" /></div>
                    <div><Label>Guest count</Label><Input type="number" value={bookForm.guest_count} onChange={(e) => setBookForm({ ...bookForm, guest_count: Number(e.target.value) })} data-testid="halls-book-guests" /></div>
                    <div><Label>Start *</Label><Input type="datetime-local" value={bookForm.start_at} onChange={(e) => setBookForm({ ...bookForm, start_at: e.target.value })} data-testid="halls-book-start" /></div>
                    <div><Label>End *</Label><Input type="datetime-local" value={bookForm.end_at} onChange={(e) => setBookForm({ ...bookForm, end_at: e.target.value })} data-testid="halls-book-end" /></div>
                    {isPlatePricing ? (
                      <div className="col-span-2 rounded-md border border-primary/40 bg-primary/5 p-3 space-y-3" data-testid="halls-book-plate-block">
                        <p className="text-xs font-medium text-primary">Per-plate F&B pricing · {selectedHall?.name}</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Veg plates</Label>
                            <Input type="number" min="0" value={bookForm.veg_plates} onChange={(e) => setBookForm({ ...bookForm, veg_plates: Number(e.target.value) })} data-testid="halls-book-veg-plates" />
                            <p className="text-[10px] text-muted-foreground mt-1">Rate ₹{bookForm.veg_plate_rate || selectedHall?.veg_plate_rate || 0}/plate</p>
                          </div>
                          <div>
                            <Label className="text-xs">Non-veg plates</Label>
                            <Input type="number" min="0" value={bookForm.non_veg_plates} onChange={(e) => setBookForm({ ...bookForm, non_veg_plates: Number(e.target.value) })} data-testid="halls-book-nonveg-plates" />
                            <p className="text-[10px] text-muted-foreground mt-1">Rate ₹{bookForm.non_veg_plate_rate || selectedHall?.non_veg_plate_rate || 0}/plate</p>
                          </div>
                          <div>
                            <Label className="text-xs">Veg rate override</Label>
                            <Input type="number" min="0" placeholder={String(selectedHall?.veg_plate_rate || 0)} value={bookForm.veg_plate_rate || ""} onChange={(e) => setBookForm({ ...bookForm, veg_plate_rate: e.target.value })} />
                          </div>
                          <div>
                            <Label className="text-xs">Non-veg rate override</Label>
                            <Input type="number" min="0" placeholder={String(selectedHall?.non_veg_plate_rate || 0)} value={bookForm.non_veg_plate_rate || ""} onChange={(e) => setBookForm({ ...bookForm, non_veg_plate_rate: e.target.value })} />
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm bg-white/60 rounded px-2 py-1.5 dark:bg-black/20" data-testid="halls-book-plate-total">
                          <span>Computed total ({(Number(bookForm.veg_plates) || 0) + (Number(bookForm.non_veg_plates) || 0)} plates)</span>
                          <span className="font-mono-plex font-semibold">{inr(computedPlateTotal)}</span>
                        </div>
                        {selectedHall?.min_plates > 0 && (Number(bookForm.veg_plates) + Number(bookForm.non_veg_plates)) < selectedHall.min_plates && (
                          <p className="text-[11px] text-[hsl(var(--warning))]">⚠ Below minimum plates ({selectedHall.min_plates}) — quote at minimum count.</p>
                        )}
                      </div>
                    ) : (
                      <div><Label>Total amount (₹)</Label><Input type="number" value={bookForm.total_amount} onChange={(e) => setBookForm({ ...bookForm, total_amount: Number(e.target.value) })} data-testid="halls-book-total" /></div>
                    )}
                    <div><Label>Deposit (₹)</Label><Input type="number" value={bookForm.deposit_amount} onChange={(e) => setBookForm({ ...bookForm, deposit_amount: Number(e.target.value) })} data-testid="halls-book-deposit" /></div>
                    <div className="col-span-2"><Label>F&B / package notes</Label><Textarea rows={2} value={bookForm.package_notes} onChange={(e) => setBookForm({ ...bookForm, package_notes: e.target.value })} data-testid="halls-book-notes" /></div>
                  </div>
                  <DialogFooter><Button onClick={saveBooking} data-testid="halls-book-save">Create booking</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </Card>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>Hall</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Purpose</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Guests</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.map((b) => (
                  <TableRow key={b.id} data-testid={`halls-booking-row-${b.id}`}>
                    <TableCell className="font-mono-plex text-xs">{b.booking_ref}</TableCell>
                    <TableCell>{halls.find((h) => h.id === b.hall_id)?.name || b.hall_id.slice(-6)}</TableCell>
                    <TableCell>{b.customer_name}<div className="text-[10px] text-muted-foreground">{b.customer_phone}</div></TableCell>
                    <TableCell>{b.purpose}</TableCell>
                    <TableCell className="text-xs">{new Date(b.start_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}<div className="text-[10px] text-muted-foreground">→ {new Date(b.end_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div></TableCell>
                    <TableCell>{b.guest_count}</TableCell>
                    <TableCell className="text-right font-mono-plex">{inr(b.total_amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        b.status === "cancelled" ? "bg-destructive/15 text-destructive" :
                        b.status === "checked_out" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" :
                        "bg-primary/15 text-primary"
                      }>{b.status.replace("_", " ")}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => window.open(`/halls/bookings/${b.id}/print`, "_blank")} data-testid={`halls-print-${b.id}`}><PrinterIcon size={12} className="mr-1" />Print</Button>
                      {canWrite && b.status === "booked" && (
                        <Button size="sm" variant="outline" onClick={() => setEditBooking(b)} data-testid={`halls-edit-booking-${b.id}`} className="ml-1"><PencilSimpleIcon size={12} className="mr-1" />Edit</Button>
                      )}
                      {canWrite && b.status === "booked" && (
                        <Button size="sm" variant="ghost" onClick={() => cancelBooking(b)} data-testid={`halls-cancel-${b.id}`} className="ml-1">Cancel</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredBookings.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground italic">No bookings.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="halls">
          <Card className="p-4 mb-4 flex justify-between">
            <div>
              <p className="label-overline">Configured halls</p>
              <p className="text-sm text-muted-foreground">Add banquet / conference / wedding halls. Set hourly + day rates.</p>
            </div>
            {canWrite && (
              <Dialog open={hallOpen} onOpenChange={(v) => { setHallOpen(v); if (!v) { setEditing(null); setHallForm(EMPTY_HALL); } }}>
                <DialogTrigger asChild><Button data-testid="halls-add-btn"><PlusIcon size={14} className="mr-1" />Add hall</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing ? "Edit hall" : "New hall"}</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Name *</Label><Input value={hallForm.name} onChange={(e) => setHallForm({ ...hallForm, name: e.target.value })} data-testid="halls-form-name" /></div>
                    <div>
                      <Label>Section</Label>
                      <Select value={hallForm.section} onValueChange={(v) => setHallForm({ ...hallForm, section: v })}>
                        <SelectTrigger data-testid="halls-form-section"><SelectValue /></SelectTrigger>
                        <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Capacity</Label><Input type="number" value={hallForm.capacity} onChange={(e) => setHallForm({ ...hallForm, capacity: Number(e.target.value) })} data-testid="halls-form-capacity" /></div>
                    <div><Label>Hourly rate (₹)</Label><Input type="number" value={hallForm.hourly_rate} onChange={(e) => setHallForm({ ...hallForm, hourly_rate: Number(e.target.value) })} data-testid="halls-form-hourly" /></div>
                    <div><Label>Day rate (₹)</Label><Input type="number" value={hallForm.day_rate} onChange={(e) => setHallForm({ ...hallForm, day_rate: Number(e.target.value) })} data-testid="halls-form-day" /></div>
                    <div className="col-span-2 rounded-md border border-border p-3">
                      <Label className="text-sm">Pricing type</Label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <button type="button" onClick={() => setHallForm({ ...hallForm, pricing_type: "fixed" })} className={`text-left rounded-md border p-2 text-xs ${hallForm.pricing_type === "fixed" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="halls-form-pricing-fixed">
                          <div className="font-medium text-sm">Fixed rate</div>
                          <div className="text-muted-foreground">Hourly / day rate as set above.</div>
                        </button>
                        <button type="button" onClick={() => setHallForm({ ...hallForm, pricing_type: "per_plate" })} className={`text-left rounded-md border p-2 text-xs ${hallForm.pricing_type === "per_plate" ? "border-primary bg-primary/5" : "border-border"}`} data-testid="halls-form-pricing-plate">
                          <div className="font-medium text-sm">Per plate (F&B)</div>
                          <div className="text-muted-foreground">Total = veg × rate + non-veg × rate.</div>
                        </button>
                      </div>
                      {hallForm.pricing_type === "per_plate" && (
                        <div className="grid grid-cols-3 gap-2 mt-3">
                          <div><Label className="text-xs">Veg plate ₹</Label><Input type="number" value={hallForm.veg_plate_rate} onChange={(e) => setHallForm({ ...hallForm, veg_plate_rate: Number(e.target.value) })} data-testid="halls-form-veg-rate" /></div>
                          <div><Label className="text-xs">Non-veg plate ₹</Label><Input type="number" value={hallForm.non_veg_plate_rate} onChange={(e) => setHallForm({ ...hallForm, non_veg_plate_rate: Number(e.target.value) })} data-testid="halls-form-nonveg-rate" /></div>
                          <div><Label className="text-xs">Min plates</Label><Input type="number" value={hallForm.min_plates} onChange={(e) => setHallForm({ ...hallForm, min_plates: Number(e.target.value) })} /></div>
                        </div>
                      )}
                    </div>
                    <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={hallForm.description} onChange={(e) => setHallForm({ ...hallForm, description: e.target.value })} /></div>
                  </div>
                  <DialogFooter><Button onClick={saveHall} data-testid="halls-form-save">{editing ? "Save" : "Add hall"}</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </Card>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Section</TableHead>
                  <TableHead>Capacity</TableHead>
                  <TableHead className="text-right">Hourly</TableHead>
                  <TableHead className="text-right">Day</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {halls.map((h) => (
                  <TableRow key={h.id} data-testid={`halls-row-${h.id}`}>
                    <TableCell><strong>{h.name}</strong><div className="text-[10px] text-muted-foreground">{h.description}</div></TableCell>
                    <TableCell><Badge variant="outline" className={SECTION_TONE[h.section] || ""}>{h.section}</Badge></TableCell>
                    <TableCell>{h.capacity}</TableCell>
                    <TableCell className="text-right font-mono-plex">{inr(h.hourly_rate)}</TableCell>
                    <TableCell className="text-right font-mono-plex">{inr(h.day_rate)}</TableCell>
                    <TableCell><Badge variant="outline" className={h.is_active ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-muted"}>{h.is_active ? "Active" : "Hidden"}</Badge></TableCell>
                    <TableCell className="text-right">
                      {canWrite && (
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => { setEditing(h); setHallForm(h); setHallOpen(true); }}><PencilSimpleIcon size={12} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => removeHall(h)} className="text-destructive"><TrashIcon size={12} /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {halls.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground italic">No halls configured.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit hall booking */}
      {editBooking && (
        <Dialog open={!!editBooking} onOpenChange={(v) => !v && setEditBooking(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="halls-edit-booking-dialog">
            <DialogHeader><DialogTitle>Edit booking · {editBooking.booking_ref}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Customer name</Label><Input value={editBooking.customer_name || ""} onChange={(e) => setEditBooking({ ...editBooking, customer_name: e.target.value })} data-testid="halls-edit-name" /></div>
              <div><Label>Phone</Label><Input value={editBooking.customer_phone || ""} onChange={(e) => setEditBooking({ ...editBooking, customer_phone: e.target.value })} data-testid="halls-edit-phone" /></div>
              <div><Label>Purpose</Label><Input value={editBooking.purpose || ""} onChange={(e) => setEditBooking({ ...editBooking, purpose: e.target.value })} /></div>
              <div><Label>Guest count</Label><Input type="number" value={editBooking.guest_count || 0} onChange={(e) => setEditBooking({ ...editBooking, guest_count: Number(e.target.value) })} /></div>
              <div><Label>Start</Label><Input type="datetime-local" value={(editBooking.start_at || "").slice(0, 16)} onChange={(e) => setEditBooking({ ...editBooking, start_at: e.target.value })} /></div>
              <div><Label>End</Label><Input type="datetime-local" value={(editBooking.end_at || "").slice(0, 16)} onChange={(e) => setEditBooking({ ...editBooking, end_at: e.target.value })} /></div>
              <div><Label>Total amount (₹)</Label><Input type="number" value={editBooking.total_amount || 0} onChange={(e) => setEditBooking({ ...editBooking, total_amount: Number(e.target.value) })} data-testid="halls-edit-total" /></div>
              <div><Label>Deposit collected (₹)</Label><Input type="number" value={editBooking.deposit_amount || 0} onChange={(e) => setEditBooking({ ...editBooking, deposit_amount: Number(e.target.value) })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={editBooking.package_notes || ""} onChange={(e) => setEditBooking({ ...editBooking, package_notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditBooking(null)}>Cancel</Button>
              <Button onClick={async () => {
                try {
                  const { id, ...updates } = editBooking;
                  await api.patch(`/halls/bookings/${id}`, updates);
                  toast.success("Booking updated");
                  setEditBooking(null);
                  loadBookings();
                } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
              }} data-testid="halls-edit-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
  function loadBookings() {
    api.get("/halls/bookings", { params: { property_id: activeId } }).then((r) => setBookings(r.data)).catch(() => {});
  }
}
